// Achievement definitions and pure unlock logic. Reuses the same per-feature
// counts the levels/XP system already computes (see xp.ts) rather than
// reading progress snapshots a second time - an achievement is just a
// threshold on a count the app already has. No new AWS/CDK surface: this is
// entirely derived from existing client-trusted progress, matching the XP
// system's own architecture decision (see CLAUDE.md).
import { XpBreakdown } from "./xp";

export type AchievementIcon = "pencil" | "glasses" | "book" | "cap";

export type Achievement = {
    id: string;
    title: string;
    // Doubles as the badge's tooltip/aria-label text.
    description: string;
    icon: AchievementIcon;
    feature: XpBreakdown["entries"][number]["feature"];
    minCount: number;
};

export const ACHIEVEMENTS: Achievement[] = [
    {
        id: "first-lesson",
        title: "First Steps",
        description: "Completed your first lesson",
        icon: "pencil",
        feature: "lessonProgress",
        minCount: 1,
    },
    {
        id: "first-kanji",
        title: "Kanji Beginner",
        description: "Marked your first kanji as known",
        icon: "glasses",
        feature: "kanjiMemory",
        minCount: 1,
    },
    {
        id: "first-word",
        title: "Word Collector",
        description: "Learned your first vocabulary word",
        icon: "book",
        feature: "vocabularyProgress",
        minCount: 1,
    },
    {
        id: "first-grammar-point",
        title: "Grammar Scholar",
        description: "Learned your first grammar point",
        icon: "cap",
        feature: "grammarProgress",
        minCount: 1,
    },
];

// Which of ACHIEVEMENTS the given breakdown currently qualifies for -
// order follows ACHIEVEMENTS, not unlock order (this module has no notion of
// "when"; see useAchievementsSeen for detecting newly-unlocked ones).
export function computeUnlockedAchievements(breakdown: XpBreakdown): Achievement[] {
    const countByFeature = new Map(breakdown.entries.map((entry) => [entry.feature, entry.count]));
    return ACHIEVEMENTS.filter((achievement) => (countByFeature.get(achievement.feature) || 0) >= achievement.minCount);
}
